import { ReactNode } from "react";
import { SortDirection } from "interfaces/api.interface";
import { Column } from "react-table";
import { Filter } from "../TableFilter/types/filter.types";

export interface UserColumnsConfig {
  [key: string]: boolean | string[];
  columnOrder?: string[];
}

export type ITableColumn<T extends object> = Column<T>;

export enum TableCellIds {
  "MENU" = "menu",
  "CHECKBOX" = "checkbox",
}

export interface TablePropsCommon<D extends object> {
  data: D[];
  filters?: { [x: string]: Filter<object> };
  /** Render filters as a horizontal bar above the table instead of a left sidebar. */
  horizontalFilters?: boolean;
  allowExport?: boolean;
  columns: Column<D>[];
  pageSize: number;
  manualPagination?: boolean;
  isLoading: boolean;
  hiddenColumns?: string[];
  defaultSort?: keyof D;
  handleColumnSort?(order: { [x: string]: SortDirection }): void;
  sort?: {
    [x: string]: string;
  };
  onRowClick?: (row: D) => void;
  onSearchSubmit?(value: string): void;
  /** Seeds the toolbar search box. Without it TableSearch falls back to the
   *  shipments slice search, which is wrong on non-shipment tables. */
  searchInitialValue?: string;
  /** Column-config key for ColumnsDropdown persistence. Without it the
   *  dropdown derives a shipments key from the route and saves over the
   *  user's shipments column config. */
  tableKey?: string;
  onRefresh?: () => void;
  erroredColumns?: { column: string; indices: number[] }[];
  downloadOptions?: { label: string; onClick: () => void }[];
  TableButtons?: React.ElementType;
  FilterComponent?: React.ElementType;
  defaultColumn?: {
    minWidth: number;
    width: number;
  };
  userColumns?: UserColumnsConfig;
  hideSearch?: boolean;
  hideDownload?: boolean;
  /** Show the "Scan & Print" icon button in the toolbar (Shipments table). */
  showScanAndPrint?: boolean;
  footerLeftContent?: ReactNode;
  /** When false, the header scrolls away with the rows instead of staying
   *  pinned. Default true (header sticky, only the body scrolls). */
  stickyHeader?: boolean;
  /** Rendered under "No results" when the table has no rows. */
  emptyState?: ReactNode;
}

export interface TablePropsManualPagination {
  pageIndex: number;
  recordCount: number;
  pageCount?: number;
  onNextPage(): void;
  onPreviousPage(): void;
  tableRowQuantityChange?(): void;
}

export type TableProps<D extends object> =
  | (TablePropsCommon<D> & {
      manualPagination: true;
    } & TablePropsManualPagination)
  | (TablePropsCommon<D> & {
      manualPagination: false;
    } & Partial<TablePropsManualPagination>);
